import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || `${import.meta.env.VITE_FIREBASE_PROJECT_ID}.firebasestorage.app`,
};

export const app = initializeApp(firebaseConfig);
export const db = getFirestore(app);
export const functions = getFunctions(app, 'us-central1');
export const auth = getAuth(app);

// Anonymous auth bridge — gives a real Firebase UID for Firestore writes.
// Phase 6: linkWithCredential() upgrades anonymous → email/password, preserving UID.
// When a returning patient signs in (non-anonymous), the UID changes and the
// promise is refreshed so Firestore writes target the correct user.
let _authReady: Promise<string> | null = null;
let _currentUid: string | null = null;

export function getAuthUid(): Promise<string> {
  // If we already have a resolved UID and the current user matches, return it fast
  const currentUser = auth.currentUser;
  if (currentUser && _currentUid === currentUser.uid) {
    return Promise.resolve(currentUser.uid);
  }

  if (!_authReady || (currentUser && _currentUid !== currentUser.uid)) {
    _authReady = new Promise((resolve, reject) => {
      const unsub = onAuthStateChanged(auth, (user) => {
        unsub(); // Only need the first emission
        if (user) {
          _currentUid = user.uid;
          resolve(user.uid);
          return;
        }
        signInAnonymously(auth)
          .then((cred) => {
            _currentUid = cred.user.uid;
            resolve(cred.user.uid);
          })
          .catch(reject);
      });
    });
  }
  return _authReady;
}

/** Reset the cached auth UID. Call this after sign-in/sign-out to force re-resolve. */
export function resetAuthUid(): void {
  _authReady = null;
  _currentUid = null;
}
